import { Fragment, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FINDING_CLASSIFICATIONS,
  FINDING_CLASSIFICATION_LABELS,
  FINDING_STATUSES,
  FINDING_STATUS_LABELS,
  type CompleteInternalAuditInput,
  type InternalAudit,
  type InternalAuditFinding,
  type InternalAuditItem,
  type InternalAuditProgress,
  type RunInternalAuditInput,
  type RunInternalAuditResult,
  type UpdateInternalAuditItemInput,
} from '@grc/types';
import { ApiRequestError, apiFetch, apiUrl } from '../../lib/api.js';
import { useOrg } from '../../lib/auth.js';
import {
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  Field,
  LoadingPane,
  PageHeader,
  ProgressBar,
  Select,
  Spinner,
  Table,
  Tabs,
  Textarea,
  formatDate,
  formatDateTime,
  useToast,
} from '../../components/ui.js';
import { AuditFormDialog } from './AuditFormDialog.js';
import { ItemAssessmentForm, ItemDialog } from './ItemDialog.js';
import { RaiseDialog } from './RaiseDialog.js';
import {
  AuditStatusBadge,
  ClassificationBadge,
  ConformityBadge,
  FindingCountBadges,
  FindingStatusBadge,
  ItemStatusBadge,
  auditKey,
  auditsListKey,
  countsOf,
  errorMessage,
  isStale,
  itemsKey,
  progressPercent,
} from './shared.js';
import './audits.css';

type TabKey = 'clauses' | 'findings' | 'plan';
type RunState = 'idle' | 'running' | 'stopping';

const RUN_BATCH: RunInternalAuditInput = { limit: 3 };
/** Pause between polls while another run holds the remaining clauses. */
const IDLE_WAIT_MS = 4000;
const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

interface Section {
  key: string;
  number: string;
  title: string;
  items: InternalAuditItem[];
}

/** Group the leaf-clause items under the top-level scope clauses (longest number prefix wins). */
function groupBySection(items: InternalAuditItem[], audit: InternalAudit): Section[] {
  const sections: Section[] = audit.scope.map((s) => ({ key: s.clauseId, number: s.number, title: s.title, items: [] }));
  const other: Section = { key: 'other', number: '', title: 'Other clauses', items: [] };
  for (const item of items) {
    let best: Section | null = null;
    for (const s of sections) {
      const match = item.clauseNumber === s.number || item.clauseNumber.startsWith(`${s.number}.`);
      if (match && (!best || s.number.length > best.number.length)) best = s;
    }
    (best ?? other).items.push(item);
  }
  return [...sections, other].filter((s) => s.items.length > 0);
}

/** What the completion dialog says about clauses the auditor has not dealt with. */
function completionNote(p: InternalAuditProgress): string {
  const parts: string[] = [];
  if (p.pending > 0) parts.push(`${p.pending} clause${p.pending === 1 ? '' : 's'} still pending`);
  if (p.failed > 0) parts.push(`${p.failed} failed`);
  if (parts.length === 0) return 'Every clause in scope has been examined or skipped.';
  return `${parts.join(' and ')} — they will be reported as not audited.`;
}

function Tile({ value, label, className = '' }: { value: string | number; label: string; className?: string }) {
  return (
    <Card className={`audit-tile ${className}`}>
      <div className="audit-tile-value">{value}</div>
      <div className="audit-tile-label">{label}</div>
    </Card>
  );
}

/** One internal audit (/internal-audits/:auditId). */
export function InternalAuditDetailPage() {
  const { auditId = '' } = useParams();
  const { org } = useOrg();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const isAdmin = org.role === 'admin';
  const canPlan = isAdmin || org.role === 'reviewer';
  const aiEnabled = org.aiEnabled;

  const auditQuery = useQuery({
    queryKey: auditKey(auditId),
    queryFn: () => apiFetch<InternalAudit>(`/internal-audits/${auditId}`),
    enabled: org.role !== 'assessor',
  });
  const itemsQuery = useQuery({
    queryKey: itemsKey(auditId),
    queryFn: () => apiFetch<InternalAuditItem[]>(`/internal-audits/${auditId}/items`),
    enabled: org.role !== 'assessor',
  });

  const [tab, setTab] = useState<TabKey>('clauses');
  const [editOpen, setEditOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [conclusion, setConclusion] = useState('');
  const [confirm, setConfirm] = useState<'approve' | 'reopen' | 'delete' | null>(null);
  const [itemId, setItemId] = useState<string | null>(null);
  const [noteFor, setNoteFor] = useState<string | null>(null);
  const [raiseFor, setRaiseFor] = useState<InternalAuditFinding | null>(null);
  const [deleteFor, setDeleteFor] = useState<InternalAuditFinding | null>(null);
  const [classFilter, setClassFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // ------------------------------------------------------------ AI run loop
  const stopRef = useRef(false);
  const [runState, setRunState] = useState<RunState>('idle');
  const [liveProgress, setLiveProgress] = useState<InternalAuditProgress | null>(null);
  const [rerunningId, setRerunningId] = useState<string | null>(null);
  useEffect(
    () => () => {
      stopRef.current = true;
    },
    [],
  );

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: itemsKey(auditId) }),
      queryClient.invalidateQueries({ queryKey: auditKey(auditId) }),
    ]);
    void queryClient.invalidateQueries({ queryKey: auditsListKey(org.orgId) });
  };

  const reportRunError = (err: unknown, fallback: string) => {
    if (err instanceof ApiRequestError && err.details?.reason === 'ai_disabled') {
      // The switch was turned off mid-run: refresh the org flag so the button hides.
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    }
    toast(errorMessage(err, fallback), 'error');
  };

  const startRun = async () => {
    if (runState !== 'idle' || !auditQuery.data) return;
    stopRef.current = false;
    setRunState('running');
    setLiveProgress(auditQuery.data.progress);
    try {
      for (;;) {
        const result = await apiFetch<RunInternalAuditResult>(`/internal-audits/${auditId}/run`, {
          method: 'POST',
          body: RUN_BATCH,
        });
        setLiveProgress(result.progress);
        await refresh();
        if (result.complete) {
          toast('The AI auditor has examined every clause in scope.', 'success');
          break;
        }
        if (stopRef.current) {
          toast('AI audit stopped. Run it again to continue with the remaining clauses.');
          break;
        }
        // Nothing claimed: another run holds the remaining clauses — wait, then ask again.
        if (result.processed.length === 0) await sleep(IDLE_WAIT_MS);
        if (stopRef.current) {
          toast('AI audit stopped.');
          break;
        }
      }
    } catch (err) {
      reportRunError(err, 'The AI auditor stopped');
      void refresh();
    } finally {
      setRunState('idle');
      setLiveProgress(null);
    }
  };
  const stopRun = () => {
    stopRef.current = true;
    setRunState('stopping');
  };

  const rerun = async (item: InternalAuditItem) => {
    if (rerunningId) return;
    setRerunningId(item.id);
    try {
      const body: RunInternalAuditInput = { limit: 1, itemId: item.id };
      const result = await apiFetch<RunInternalAuditResult>(`/internal-audits/${auditId}/run`, {
        method: 'POST',
        body,
      });
      const outcome = result.processed[0];
      if (!outcome) toast('That clause is being examined by another run.');
      else if (outcome.status === 'failed') toast(outcome.error ?? 'The AI auditor could not process this clause', 'error');
      else toast(`${item.clauseNumber} re-examined: ${outcome.findingCount} finding${outcome.findingCount === 1 ? '' : 's'}.`, 'success');
      await refresh();
    } catch (err) {
      reportRunError(err, 'Could not re-run the AI auditor');
    } finally {
      setRerunningId(null);
    }
  };

  // ------------------------------------------------------------- mutations
  const patchItem = useMutation({
    mutationFn: ({ item, body }: { item: InternalAuditItem; body: UpdateInternalAuditItemInput }) =>
      apiFetch<InternalAuditItem>(`/internal-audits/${auditId}/items/${item.id}`, { method: 'PATCH', body }),
    onSuccess: (_updated, { body }) => {
      void refresh();
      toast(body.status === 'skipped' ? 'Clause skipped.' : 'Clause reset to pending.', 'success');
    },
    onError: (err) => toast(errorMessage(err, 'Could not update the clause'), 'error'),
  });

  const complete = useMutation({
    mutationFn: () => {
      const body: CompleteInternalAuditInput = { conclusion: conclusion.trim() };
      return apiFetch<InternalAudit>(`/internal-audits/${auditId}/complete`, { method: 'POST', body });
    },
    onSuccess: () => {
      void refresh();
      toast('Audit completed. An admin can now approve it.', 'success');
      setCompleteOpen(false);
    },
    onError: (err) => toast(errorMessage(err, 'Could not complete the audit'), 'error'),
  });

  const transition = useMutation({
    mutationFn: (action: 'approve' | 'reopen') =>
      apiFetch<InternalAudit>(`/internal-audits/${auditId}/${action}`, { method: 'POST' }),
    onSuccess: (_a, action) => {
      void refresh();
      toast(action === 'approve' ? 'Audit approved.' : 'Audit reopened.', 'success');
      setConfirm(null);
    },
    onError: (err) => toast(errorMessage(err, 'Could not update the audit'), 'error'),
  });

  const remove = useMutation({
    mutationFn: () => apiFetch<{ ok: true }>(`/internal-audits/${auditId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: auditsListKey(org.orgId) });
      toast('Audit deleted.', 'success');
      navigate('/internal-audits');
    },
    onError: (err) => toast(errorMessage(err, 'Could not delete the audit'), 'error'),
  });

  const deleteFinding = useMutation({
    mutationFn: (finding: InternalAuditFinding) =>
      apiFetch<{ ok: true }>(`/internal-audits/${auditId}/findings/${finding.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void refresh();
      toast('Finding deleted.', 'success');
      setDeleteFor(null);
    },
    onError: (err) => toast(errorMessage(err, 'Could not delete the finding'), 'error'),
  });

  // --------------------------------------------------------------- derived
  const audit = auditQuery.data;
  const items = useMemo(() => itemsQuery.data ?? [], [itemsQuery.data]);
  const sections = useMemo(() => (audit ? groupBySection(items, audit) : []), [items, audit]);
  const allFindings = useMemo(
    () =>
      items.flatMap((item) =>
        item.findings.map((f) => ({ finding: f, item })).filter(({ finding }) =>
          (!classFilter || finding.classification === classFilter) &&
          (!statusFilter || finding.status === statusFilter),
        ),
      ),
    [items, classFilter, statusFilter],
  );
  const selectedItem = items.find((i) => i.id === itemId) ?? null;
  const noteItem = items.find((i) => i.id === noteFor) ?? null;

  if (org.role === 'assessor') {
    return (
      <EmptyState
        title="Members only"
        message="Internal audits are visible to members of the organisation, not to external assessors."
      />
    );
  }
  if (auditQuery.isLoading || itemsQuery.isLoading) return <LoadingPane label="Loading internal audit" />;
  if (!audit) {
    return (
      <EmptyState
        title="Audit not found"
        message={auditQuery.error ? errorMessage(auditQuery.error, 'This audit could not be loaded.') : undefined}
        action={
          <Link to="/internal-audits">
            <Button variant="secondary">All internal audits</Button>
          </Link>
        }
      />
    );
  }

  const auditOpen = audit.status === 'planned' || audit.status === 'in_progress';
  const canEdit = canPlan && audit.status !== 'approved';
  const canRun = canPlan && aiEnabled && auditOpen;
  const busy = runState !== 'idle';
  const progress = liveProgress ?? audit.progress;
  const nothingToRun = audit.progress.pending === 0 && audit.progress.running === 0;

  const openItem = (id: string) => setItemId(id);
  const report = (format: 'html' | 'pdf' | 'docx' | 'markdown', label: string) => (
    <a key={format} href={apiUrl(`/internal-audits/${auditId}/report?format=${format}`)}>
      <Button size="sm" variant="secondary">
        {label}
      </Button>
    </a>
  );

  return (
    <div>
      <p>
        <Link to="/internal-audits">← All internal audits</Link>
      </p>
      <PageHeader
        title={audit.name}
        subtitle={
          <>
            {audit.standardName} <Badge>{audit.versionLabel}</Badge> <AuditStatusBadge status={audit.status} />
            {audit.leadAuditorName && <> · Lead auditor {audit.leadAuditorName}</>}
            {audit.plannedOn && <> · planned {formatDate(audit.plannedOn)}</>}
            {audit.assessmentId && (
              <>
                {' '}
                · source <Link to={`/assessments/${audit.assessmentId}`}>{audit.assessmentName ?? 'assessment'}</Link>
              </>
            )}
          </>
        }
        actions={
          <div className="audit-actions">
            {canPlan &&
              aiEnabled &&
              (runState === 'idle' ? (
                <Button
                  onClick={() => void startRun()}
                  disabled={!auditOpen || nothingToRun || rerunningId !== null}
                  title={
                    !auditOpen
                      ? 'The AI auditor only runs on a planned or in-progress audit'
                      : nothingToRun
                        ? 'Every clause has been examined — re-run individual clauses from the table'
                        : undefined
                  }
                >
                  Run AI audit
                </Button>
              ) : (
                <Button variant="danger" onClick={stopRun} loading={runState === 'stopping'}>
                  {runState === 'stopping' ? 'Stopping…' : 'Stop'}
                </Button>
              ))}
            {canPlan && auditOpen && (
              <Button
                variant="secondary"
                disabled={busy}
                onClick={() => {
                  setConclusion(audit.conclusion);
                  setCompleteOpen(true);
                }}
              >
                Complete audit
              </Button>
            )}
            {isAdmin && audit.status === 'completed' && (
              <Button onClick={() => setConfirm('approve')}>Approve</Button>
            )}
            {isAdmin && (audit.status === 'completed' || audit.status === 'approved') && (
              <Button variant="secondary" onClick={() => setConfirm('reopen')}>
                Reopen
              </Button>
            )}
            {canEdit && (
              <Button variant="secondary" disabled={busy} onClick={() => setEditOpen(true)}>
                Edit details
              </Button>
            )}
            {isAdmin && audit.status === 'planned' && (
              <Button variant="ghost" disabled={busy} onClick={() => setConfirm('delete')}>
                Delete
              </Button>
            )}
            <span className="audit-report-links">
              Report
              {report('html', 'HTML')}
              {report('pdf', 'PDF')}
              {report('docx', 'DOCX')}
              {report('markdown', 'Markdown')}
            </span>
          </div>
        }
      />

      {!aiEnabled && (
        <p className="audit-note">
          AI assistance is turned off in Organisation settings, so the AI auditor cannot run. Clauses
          can still be audited by hand: open a clause, record its result and add findings.
        </p>
      )}

      {busy && (
        <Card className="audit-run-panel">
          <div className="audit-run-head">
            <Spinner label="AI auditor running" />
            <strong>{runState === 'stopping' ? 'Finishing the current clauses…' : 'AI auditor running'}</strong>
            <span className="audit-muted grow">
              {progress.done + progress.failed + progress.skipped} of {progress.total} clauses examined
              {progress.failed > 0 && `, ${progress.failed} failed`}
              {progress.pending > 0 && `, ${progress.pending} to go`}
            </span>
            <Button size="sm" variant="secondary" onClick={stopRun} disabled={runState === 'stopping'}>
              Stop
            </Button>
          </div>
          <ProgressBar percent={progressPercent(progress)} label="AI audit progress" />
        </Card>
      )}

      <div className="audit-tiles">
        <Tile
          value={`${audit.progress.done + audit.progress.skipped}/${audit.progress.total}`}
          label={`clauses audited${audit.progress.failed > 0 ? ` · ${audit.progress.failed} failed` : ''}${
            audit.progress.skipped > 0 ? ` · ${audit.progress.skipped} skipped` : ''
          }`}
        />
        <Tile value={audit.counts.major} label="Major nonconformities" className="major" />
        <Tile value={audit.counts.minor} label="Minor nonconformities" className="minor" />
        <Tile value={audit.counts.observation} label="Observations" className="observation" />
        <Tile value={audit.counts.ofi} label="Opportunities for improvement" className="ofi" />
        <Tile value={audit.counts.conforming} label="Conforming findings" className="conforming" />
      </div>

      <Tabs<TabKey>
        tabs={[
          { key: 'clauses', label: `Clauses (${items.length})` },
          { key: 'findings', label: `Findings (${items.reduce((n, i) => n + i.findings.length, 0)})` },
          { key: 'plan', label: 'Plan & conclusion' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'clauses' && (
        <>
          <Table
            head={
              <>
                <th>Clause</th>
                <th>Title</th>
                <th>Result</th>
                <th>Findings</th>
                <th>Evidence examined</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </>
            }
          >
            {sections.map((section) => (
              <Fragment key={section.key}>
                <tr className="audit-section-row">
                  <th colSpan={6} scope="colgroup">
                    {section.number} {section.title}
                  </th>
                </tr>
                {section.items.map((item) => {
                  const stale = isStale(item);
                  const counts = countsOf(item.findings);
                  const files = item.examined.evidence;
                  const responses = item.examined.responses.length;
                  return (
                    <tr key={item.id}>
                      <td className="audit-nowrap">{item.clauseNumber}</td>
                      <td>
                        <button type="button" className="audit-link" onClick={() => openItem(item.id)}>
                          {item.clauseTitle}
                        </button>
                        {item.error && <div className="audit-error-inline">{item.error}</div>}
                      </td>
                      <td>
                        <span className="audit-badges">
                          {item.status !== 'done' && <ItemStatusBadge status={item.status} />}
                          {stale && <Badge tone="warning">stale</Badge>}
                          {item.conformity !== 'not_audited' && <ConformityBadge conformity={item.conformity} />}
                        </span>
                      </td>
                      <td>
                        {item.findings.length === 0 ? (
                          '—'
                        ) : (
                          <span className="audit-badges">
                            <FindingCountBadges counts={counts} />
                            {counts.conforming > 0 && (
                              <Badge tone="success">
                                <span aria-label={`${counts.conforming} conforming`}>C {counts.conforming}</span>
                              </Badge>
                            )}
                          </span>
                        )}
                      </td>
                      <td>
                        {files.length === 0 && responses === 0 ? (
                          '—'
                        ) : (
                          <details className="audit-examined-cell">
                            <summary title={files.join('\n') || 'No evidence files'}>
                              {files.length} file{files.length === 1 ? '' : 's'} · {responses} response
                              {responses === 1 ? '' : 's'}
                            </summary>
                            {files.length > 0 && (
                              <ul>
                                {files.map((name, i) => (
                                  <li key={`${name}-${i}`}>{name}</li>
                                ))}
                              </ul>
                            )}
                          </details>
                        )}
                      </td>
                      <td>
                        <div className="audit-row-actions">
                          <Button size="sm" variant="secondary" onClick={() => openItem(item.id)}>
                            View
                          </Button>
                          {canRun && (['done', 'failed', 'skipped'].includes(item.status) || stale) && (
                            <Button
                              size="sm"
                              variant="secondary"
                              loading={rerunningId === item.id}
                              disabled={busy || (rerunningId !== null && rerunningId !== item.id)}
                              onClick={() => void rerun(item)}
                            >
                              Re-run
                            </Button>
                          )}
                          {canEdit && item.status !== 'running' && item.status !== 'skipped' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={patchItem.isPending}
                              onClick={() => patchItem.mutate({ item, body: { status: 'skipped' } })}
                            >
                              Skip
                            </Button>
                          )}
                          {canEdit && (item.status === 'failed' || item.status === 'skipped' || stale) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={patchItem.isPending}
                              onClick={() => patchItem.mutate({ item, body: { status: 'pending' } })}
                            >
                              Reset to pending
                            </Button>
                          )}
                          {canEdit && (
                            <Button size="sm" variant="ghost" onClick={() => setNoteFor(item.id)}>
                              Note
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </Fragment>
            ))}
          </Table>
          {items.length === 0 && <p className="audit-muted" style={{ padding: 16 }}>No clauses are in scope.</p>}
        </>
      )}

      {tab === 'findings' && (
        <>
          <div className="audit-filters">
            <Select aria-label="Filter by classification" value={classFilter} onChange={(e) => setClassFilter(e.target.value)}>
              <option value="">All classifications</option>
              {FINDING_CLASSIFICATIONS.map((c) => (
                <option key={c} value={c}>
                  {FINDING_CLASSIFICATION_LABELS[c]}
                </option>
              ))}
            </Select>
            <Select aria-label="Filter by status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All statuses</option>
              {FINDING_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {FINDING_STATUS_LABELS[s]}
                </option>
              ))}
            </Select>
          </div>
          <Table
            head={
              <>
                <th>Clause</th>
                <th>Classification</th>
                <th>Finding</th>
                <th>Status</th>
                <th>Corrective action</th>
                <th>Recorded by</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </>
            }
          >
            {allFindings.map(({ finding, item }) => (
              <tr key={finding.id}>
                <td className="audit-nowrap">{finding.clauseNumber}</td>
                <td>
                  <ClassificationBadge classification={finding.classification} />
                </td>
                <td>
                  <button type="button" className="audit-link" onClick={() => openItem(item.id)}>
                    {finding.title}
                  </button>
                  {finding.finding && (
                    <div className="audit-muted">
                      {finding.finding.length > 160 ? `${finding.finding.slice(0, 160)}…` : finding.finding}
                    </div>
                  )}
                </td>
                <td>
                  <FindingStatusBadge status={finding.status} />
                </td>
                <td>
                  {finding.correctiveActionRecordId ? (
                    finding.correctiveActionName === '(record deleted)' ? (
                      <span className="audit-muted">(record deleted)</span>
                    ) : (
                      <Link
                        to={`/registers/corrective-actions?record=${encodeURIComponent(finding.correctiveActionRecordId)}`}
                      >
                        {finding.correctiveActionName ?? 'Open'}
                      </Link>
                    )
                  ) : (
                    '—'
                  )}
                </td>
                <td>
                  {finding.aiGenerated ? <Badge tone="purple">AI-drafted</Badge> : finding.createdByName ?? '—'}
                </td>
                <td>
                  <div className="audit-row-actions">
                    <Button size="sm" variant="secondary" onClick={() => openItem(item.id)}>
                      Open clause
                    </Button>
                    {canEdit && finding.classification !== 'conforming' && !finding.correctiveActionRecordId && (
                      <Button size="sm" variant="ghost" onClick={() => setRaiseFor(finding)}>
                        Raise corrective action
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </Table>
          {allFindings.length === 0 && (
            <p className="audit-muted" style={{ padding: 16 }}>
              {classFilter || statusFilter ? 'No findings match these filters.' : 'No findings recorded yet.'}
            </p>
          )}
        </>
      )}

      {tab === 'plan' && (
        <Card>
          <dl className="audit-plan">
            <dt>Standard</dt>
            <dd>
              {audit.standardName} {audit.versionLabel}
            </dd>
            <dt>Scope</dt>
            <dd>
              <ul>
                {audit.scope.map((s) => (
                  <li key={s.clauseId}>
                    {s.number} {s.title}
                  </li>
                ))}
              </ul>
            </dd>
            <dt>Objectives</dt>
            <dd>{audit.objectives || '—'}</dd>
            <dt>Criteria</dt>
            <dd>{audit.criteria || '—'}</dd>
            <dt>Source assessment</dt>
            <dd>
              {audit.assessmentId ? (
                <Link to={`/assessments/${audit.assessmentId}`}>{audit.assessmentName ?? audit.assessmentId}</Link>
              ) : (
                'None — documents and registers only'
              )}
            </dd>
            <dt>Lead auditor</dt>
            <dd>{audit.leadAuditorName ?? '—'}</dd>
            <dt>Auditees / people interviewed</dt>
            <dd>{audit.auditees || '—'}</dd>
            <dt>Auditor independence</dt>
            <dd>{audit.independenceNote || 'No constraint recorded'}</dd>
            <dt>Planned date</dt>
            <dd>{formatDate(audit.plannedOn)}</dd>
            <dt>Audit dates</dt>
            <dd>
              {audit.startedAt
                ? `started ${formatDateTime(audit.startedAt)}`
                : audit.completedAt
                  ? `completed ${formatDateTime(audit.completedAt)}`
                  : 'not started'}
              {audit.startedAt && audit.completedAt && ` · completed ${formatDateTime(audit.completedAt)}`}
            </dd>
            <dt>Evidence file contents</dt>
            <dd>{audit.includeEvidenceText ? 'Read by the AI auditor' : 'File names only'}</dd>
            <dt>Conclusion</dt>
            <dd>{audit.conclusion || '—'}</dd>
            <dt>Approval</dt>
            <dd>
              {audit.approvedByName
                ? `Approved by ${audit.approvedByName} on ${formatDateTime(audit.approvedAt)}`
                : 'Not yet approved'}
            </dd>
            <dt>Planned by</dt>
            <dd>
              {audit.createdByName} · {formatDateTime(audit.createdAt)}
            </dd>
          </dl>
        </Card>
      )}

      {/* ------------------------------------------------------- dialogs */}
      <AuditFormDialog open={editOpen} onClose={() => setEditOpen(false)} audit={audit} />

      <ItemDialog
        audit={audit}
        item={selectedItem}
        open={itemId !== null}
        onClose={() => setItemId(null)}
        canEdit={canEdit}
        canRerun={canRun && !busy}
        rerunning={rerunningId !== null && rerunningId === itemId}
        onRerun={(item) => void rerun(item)}
        onRaise={setRaiseFor}
        onDelete={setDeleteFor}
      />

      <Dialog
        open={noteFor !== null}
        onClose={() => setNoteFor(null)}
        title={noteItem ? `${noteItem.clauseNumber} ${noteItem.clauseTitle}` : 'Auditor note'}
      >
        {noteItem && (
          <>
            <ItemAssessmentForm auditId={auditId} item={noteItem} onSaved={() => setNoteFor(null)} />
            <div className="dialog-actions" style={{ marginTop: 0 }}>
              <Button variant="secondary" onClick={() => setNoteFor(null)}>
                Close
              </Button>
            </div>
          </>
        )}
      </Dialog>

      <RaiseDialog audit={audit} finding={raiseFor} open={raiseFor !== null} onClose={() => setRaiseFor(null)} />

      <Dialog open={deleteFor !== null} onClose={() => setDeleteFor(null)} title="Delete this finding?">
        {deleteFor && (
          <>
            <p>
              “{deleteFor.title}” ({FINDING_CLASSIFICATION_LABELS[deleteFor.classification].toLowerCase()}) will be
              removed from the audit. A corrective action already raised from it stays in the register.
            </p>
            <div className="dialog-actions">
              <Button variant="secondary" onClick={() => setDeleteFor(null)}>
                Cancel
              </Button>
              <Button variant="danger" loading={deleteFinding.isPending} onClick={() => deleteFinding.mutate(deleteFor)}>
                Delete finding
              </Button>
            </div>
          </>
        )}
      </Dialog>

      <Dialog open={completeOpen} onClose={() => setCompleteOpen(false)} title="Complete the audit">
        <form
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            if (conclusion.trim() && !complete.isPending) complete.mutate();
          }}
        >
          <p>{completionNote(audit.progress)}</p>
          <Field
            label="Conclusion"
            required
            hint="State whether the audited controls conform to the requirements and are effective, and any limitation of the audit"
          >
            {(props) => (
              <Textarea
                {...props}
                required
                rows={5}
                maxLength={5000}
                value={conclusion}
                onChange={(e) => setConclusion(e.target.value)}
              />
            )}
          </Field>
          <div className="dialog-actions">
            <Button variant="secondary" onClick={() => setCompleteOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={complete.isPending} disabled={!conclusion.trim()}>
              Complete audit
            </Button>
          </div>
        </form>
      </Dialog>

      <Dialog
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        title={confirm === 'approve' ? 'Approve this audit?' : confirm === 'reopen' ? 'Reopen this audit?' : 'Delete this audit?'}
      >
        {confirm === 'approve' && (
          <p>
            Approval records you as the approver and freezes the audit. A second person must approve
            an audit they led where another admin is available.
          </p>
        )}
        {confirm === 'reopen' && (
          <p>The audit returns to in progress; its approval and completion date are cleared.</p>
        )}
        {confirm === 'delete' && (
          <p>The planned audit and its clauses and findings are deleted. This cannot be undone.</p>
        )}
        <div className="dialog-actions">
          <Button variant="secondary" onClick={() => setConfirm(null)}>
            Cancel
          </Button>
          {confirm === 'delete' ? (
            <Button variant="danger" loading={remove.isPending} onClick={() => remove.mutate()}>
              Delete audit
            </Button>
          ) : (
            <Button loading={transition.isPending} onClick={() => confirm && transition.mutate(confirm)}>
              {confirm === 'approve' ? 'Approve' : 'Reopen'}
            </Button>
          )}
        </div>
      </Dialog>
    </div>
  );
}
